
import React, { Suspense, lazy, useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { prefetchRoutes, prefetchRoute } from "./utils/prefetchRoutes";

// Import only the Index page eagerly - this is our landing page
import Index from "./pages/Index";

// Lazy load all other pages to reduce initial bundle size
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const Chat = lazy(() => import("./pages/Chat"));
const Profile = lazy(() => import("./pages/Profile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Features = lazy(() => import("./pages/Features"));
const Timeline = lazy(() => import("./pages/Timeline"));
const BrightPlan = lazy(() => import("./pages/EarlyAccess"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Demo = lazy(() => import("./pages/Demo"));
const EarlyAccessPage = lazy(() => import("./pages/EarlyAccessPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SuccessRedirect = lazy(() => import("./pages/SuccessRedirect"));
const EarlyAccessRedirect = lazy(() => import("./pages/EarlyAccessRedirect"));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-white">
    <div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-purple-600"></div>
  </div>
);

const queryClient = new QueryClient();

const App = () => {
  // Start prefetching routes after initial render
  useEffect(() => {
    // Begin prefetching other routes in the background
    prefetchRoutes();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Index route is eagerly loaded for faster initial render */}
              <Route path="/" element={<Index />} />
              
              {/* All other routes are lazy loaded */}
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/features" element={<Features />} />
              <Route path="/timeline" element={<Timeline />} />
              <Route path="/bright-plan" element={<BrightPlan />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/early-access" element={<EarlyAccessRedirect />} />
              <Route path="/success" element={<SuccessRedirect />} />
              <Route path="/login" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
